import { useEffect, useState } from "react";
import { api, can } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Copy, Ticket, ScrollText, Shield, Database, Download, Upload } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [logs, setLogs] = useState([]);
  const [dc, setDc] = useState({ announcements_webhook_url:"", urgent_webhook_url:"", excuse_webhook_url:"", memur_excuse_webhook_url:"" });
  const [dcConfigured, setDcConfigured] = useState({ announcements_webhook_url:false, urgent_webhook_url:false, excuse_webhook_url:false, memur_excuse_webhook_url:false });
  const [testResult, setTestResult] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [restoreOverwrite, setRestoreOverwrite] = useState(false);
  const [role, setRole] = useState("member");
  const canFounder = can(user.role, "founder");

  const load = async () => {
    api.get("/invites").then(r => setInvites(r.data));
    api.get("/audit-logs").then(r => setLogs(r.data));
    api.get("/discord-config").then(r => {
      setDcConfigured({
        announcements_webhook_url: !!r.data.announcements_webhook_url,
        urgent_webhook_url: !!r.data.urgent_webhook_url,
        excuse_webhook_url: !!r.data.excuse_webhook_url,
        memur_excuse_webhook_url: !!r.data.memur_excuse_webhook_url,
      });
      setDc({ ...dc, announcements_webhook_url: "", urgent_webhook_url: "", excuse_webhook_url: "", memur_excuse_webhook_url: "" });
    }).catch(()=>{});
    api.get("/admin/db-stats").then(r => setDbStats(r.data)).catch(()=>{});
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const downloadBackup = async () => {
    try {
      const res = await api.get("/admin/backup");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      a.href = url; a.download = `deulsra-yedek-${ts}.json`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success("Yedek indirildi");
    } catch (e) { toast.error(e.response?.data?.detail || "Yedek alınamadı"); }
  };

  const uploadBackup = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data || typeof data !== "object") throw new Error("Geçersiz dosya");
      if (restoreOverwrite && !window.confirm("ÜZERİNE YAZILACAK: mevcut tüm veriler silinip yedekten yüklenecek. Emin misin?")) return;
      const res = await api.post("/admin/restore", { data, overwrite: restoreOverwrite });
      toast.success("Yedek yüklendi: " + JSON.stringify(res.data.restored));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Yükleme başarısız"); }
  };

  const testDc = async () => {
    try {
      const res = await api.post("/discord-config/test");
      setTestResult(res.data);
      const anyOk = Object.values(res.data).some(v => v === "OK");
      toast[anyOk ? "success" : "error"](anyOk ? "Test mesajı gönderildi" : "Test başarısız");
    } catch (e) { toast.error(e.response?.data?.detail || "Test başarısız"); }
  };

  const safeCopy = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_) { /* ignore */ }
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch (_) { return false; }
  };

  const createInvite = async () => {
    try {
      const res = await api.post("/invites", { initial_role: role });
      const ok = await safeCopy(res.data.code);
      toast.success(ok ? `Davet kodu kopyalandı: ${res.data.code}` : `Davet kodu: ${res.data.code}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const copyCode = async (code) => {
    const ok = await safeCopy(code);
    toast.success(ok ? "Kod kopyalandı" : `Kopyalanamadı: ${code}`);
  };

  const saveDc = async () => {
    try {
      const payload = {};
      for (const k of ["announcements_webhook_url", "urgent_webhook_url", "excuse_webhook_url", "memur_excuse_webhook_url"]) {
        if (dc[k]) payload[k] = dc[k];
      }
      await api.put("/discord-config", payload);
      toast.success("Discord ayarları kaydedildi");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Başarısız"); }
  };

  const isExpired = (i) => new Date(i.expires_at) < new Date();

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-6xl">
      <header>
        <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">// ADMIN CONSOLE</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Yönetim</h1>
        <p className="text-zinc-500 mt-2">Davet kodları, işlem geçmişi ve sistem yapılandırması.</p>
      </header>

      <Tabs defaultValue="invites">
        <TabsList className="bg-zinc-950 border border-zinc-900">
          <TabsTrigger value="invites" data-testid="admin-tab-invites"><Ticket className="w-4 h-4 mr-2" />Davet Kodları</TabsTrigger>
          <TabsTrigger value="logs" data-testid="admin-tab-logs"><ScrollText className="w-4 h-4 mr-2" />İşlem Geçmişi</TabsTrigger>
          {canFounder && <TabsTrigger value="backup" data-testid="admin-tab-backup"><Database className="w-4 h-4 mr-2" />Yedekleme</TabsTrigger>}
          {canFounder && <TabsTrigger value="discord" data-testid="admin-tab-discord"><Shield className="w-4 h-4 mr-2" />Discord</TabsTrigger>}
        </TabsList>

        <TabsContent value="invites" className="mt-4 space-y-4">
          <Card className="bg-zinc-950 border-zinc-900 p-5">
            <div className="flex items-end gap-3 flex-wrap">
              <div><Label className="text-xs font-mono">BAŞLANGIÇ ROLÜ</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="invite-role-select" className="bg-zinc-900 border-zinc-800 w-40 mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    <SelectItem value="candidate">Aday / Dost</SelectItem>
                    <SelectItem value="member">Ekip Üyesi</SelectItem>
                    {canFounder && <SelectItem value="officer">Yetkili</SelectItem>}
                    {canFounder && <SelectItem value="admin">Yönetici</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={createInvite} data-testid="create-invite-button" className="bg-rose-600 hover:bg-rose-700">
                <Plus className="w-4 h-4 mr-2" /> Kod Oluştur (10 dk)
              </Button>
            </div>
          </Card>

          <Card className="bg-zinc-950 border-zinc-900 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/50">
                <tr className="text-left text-[10px] font-mono text-zinc-500 tracking-widest">
                  <th className="px-4 py-3">KOD</th><th className="px-4 py-3">ROL</th><th className="px-4 py-3">OLUŞTURAN</th>
                  <th className="px-4 py-3">SON GEÇERLİ</th><th className="px-4 py-3">DURUM</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {invites.map(i => (
                  <tr key={i.id} className="border-t border-zinc-900" data-testid={`invite-row-${i.id}`}>
                    <td className="px-4 py-3 font-mono text-rose-400 tracking-wider">{i.code}</td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-400">{i.initial_role}</td>
                    <td className="px-4 py-3 text-xs">{i.created_by_name}</td>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-500">{new Date(i.expires_at).toLocaleString("tr-TR")}</td>
                    <td className="px-4 py-3">
                      {i.used ? <span className="text-[10px] font-mono px-2 py-0.5 border bg-zinc-800/50 text-zinc-400 border-zinc-700">KULLANILDI</span>
                        : isExpired(i) ? <span className="text-[10px] font-mono px-2 py-0.5 border bg-rose-900/30 text-rose-400 border-rose-800">SÜRESİ DOLDU</span>
                        : <span className="text-[10px] font-mono px-2 py-0.5 border bg-emerald-900/30 text-emerald-400 border-emerald-800">AKTİF</span>}
                    </td>
                    <td className="px-4 py-3">
                      {!i.used && !isExpired(i) && <Button size="sm" variant="ghost" onClick={()=>copyCode(i.code)} data-testid={`copy-invite-${i.id}`}><Copy className="w-4 h-4" /></Button>}
                    </td>
                  </tr>
                ))}
                {invites.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">Kayıt yok.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <Card className="bg-zinc-950 border-zinc-900 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/50">
                <tr className="text-left text-[10px] font-mono text-zinc-500 tracking-widest">
                  <th className="px-4 py-3">ZAMAN</th><th className="px-4 py-3">AKTÖR</th>
                  <th className="px-4 py-3">EYLEM</th><th className="px-4 py-3">DETAY</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id} className="border-t border-zinc-900" data-testid={`log-row-${l.id}`}>
                    <td className="px-4 py-3 text-xs font-mono text-zinc-500">{new Date(l.created_at).toLocaleString("tr-TR")}</td>
                    <td className="px-4 py-3 text-xs">{l.actor_name}</td>
                    <td className="px-4 py-3 text-xs font-mono text-rose-400">{l.action}</td>
                    <td className="px-4 py-3 text-xs text-zinc-400">{l.details || "—"}</td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-zinc-500">Kayıt yok.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {canFounder && (
          <TabsContent value="backup" className="mt-4">
            <Card className="bg-zinc-950 border-zinc-900 p-6 space-y-5 max-w-2xl">
              <div className="flex items-center justify-between">
                <div className="text-xs font-mono text-zinc-500 tracking-widest">// VERİ YEDEKLEME</div>
                {dbStats && (
                  <span className="text-[10px] font-mono px-2 py-0.5 border bg-zinc-900/50 text-zinc-400 border-zinc-800" data-testid="db-total-badge">
                    Toplam {dbStats.total} kayıt
                  </span>
                )}
              </div>

              {dbStats && (
                <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
                  {Object.entries(dbStats.counts).map(([k, v]) => (
                    <div key={k} className="bg-zinc-900/40 p-2 border-l-2 border-zinc-800">
                      <div className="text-zinc-500">{k}</div>
                      <div className="text-white text-base">{v}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-3">
                <div className="text-sm font-semibold text-zinc-200">Yedek İndir</div>
                <div className="text-xs text-zinc-500">Tüm verinin (kullanıcı, davet, kural, duyuru, mazeret, bildirim, işlem geçmişi, IBAN, Discord ayarları) JSON dosyası olarak bilgisayarına indirilir.</div>
                <Button onClick={downloadBackup} data-testid="backup-download-button" className="bg-emerald-600 hover:bg-emerald-700">
                  <Download className="w-4 h-4 mr-2" /> JSON Yedek İndir
                </Button>
              </div>

              <div className="space-y-3 pt-4 border-t border-zinc-900">
                <div className="text-sm font-semibold text-zinc-200">Yedekten Geri Yükle</div>
                <div className="text-xs text-zinc-500">Daha önce indirdiğin JSON dosyasını yükle. Varsayılan: eksik kayıtları ekler (mevcut veriye dokunmaz).</div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={restoreOverwrite} onChange={e=>setRestoreOverwrite(e.target.checked)} data-testid="backup-overwrite-checkbox" />
                  <span className={restoreOverwrite ? "text-rose-400" : "text-zinc-500"}>ÜZERİNE YAZ (tehlikeli — mevcut tüm veriyi siler)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="file" accept=".json,application/json" onChange={e=>uploadBackup(e.target.files?.[0])} data-testid="backup-upload-input" className="text-xs text-zinc-400 file:mr-2 file:py-2 file:px-3 file:border-0 file:bg-rose-600 file:text-white file:font-semibold hover:file:bg-rose-700 file:cursor-pointer" />
                </label>
              </div>

              <div className="text-[11px] text-zinc-500 border-l-2 border-emerald-500/50 pl-3 bg-emerald-950/10 py-2 space-y-1">
                <div className="text-emerald-400 font-semibold">Güvenlik notu</div>
                <div>• Uygulama kodu hiçbir zaman verini otomatik silmez.</div>
                <div>• Backend restart / hot reload / supervisor restart veri kaybına neden olmaz.</div>
                <div>• Kritik değişiklikten önce yedek indirmen önerilir.</div>
                <div>• Yedek JSON dosyasında webhook URL'leri gibi hassas veriler var; güvenli sakla.</div>
              </div>
            </Card>
          </TabsContent>
        )}

        {canFounder && (
          <TabsContent value="discord" className="mt-4">
            <Card className="bg-zinc-950 border-zinc-900 p-6 space-y-4 max-w-xl">
              <div className="flex items-center justify-between">
                <div className="text-xs font-mono text-zinc-500 tracking-widest">// DISCORD WEBHOOK AYARLARI</div>
              </div>
              <div><Label className="text-xs font-mono flex items-center gap-2">
                  DUYURU WEBHOOK URL
                  {dcConfigured.announcements_webhook_url && <span className="text-[10px] text-emerald-400">● kayıtlı</span>}
                </Label>
                <Input type="password" value={dc.announcements_webhook_url} onChange={e=>setDc({...dc,announcements_webhook_url:e.target.value})} placeholder="Değiştirmek için gir" data-testid="dc-announce-input" className="bg-zinc-900 border-zinc-800 mt-1.5 font-mono" /></div>
              <div><Label className="text-xs font-mono flex items-center gap-2">
                  ACİL WEBHOOK URL
                  {dcConfigured.urgent_webhook_url && <span className="text-[10px] text-emerald-400">● kayıtlı</span>}
                </Label>
                <Input type="password" value={dc.urgent_webhook_url} onChange={e=>setDc({...dc,urgent_webhook_url:e.target.value})} placeholder="Değiştirmek için gir" data-testid="dc-urgent-input" className="bg-zinc-900 border-zinc-800 mt-1.5 font-mono" /></div>
              <div><Label className="text-xs font-mono flex items-center gap-2">
                  MAZERET WEBHOOK URL
                  {dcConfigured.excuse_webhook_url && <span className="text-[10px] text-emerald-400">● kayıtlı</span>}
                </Label>
                <Input type="password" value={dc.excuse_webhook_url} onChange={e=>setDc({...dc,excuse_webhook_url:e.target.value})} placeholder="Değiştirmek için gir" data-testid="dc-excuse-input" className="bg-zinc-900 border-zinc-800 mt-1.5 font-mono" /></div>
              <div><Label className="text-xs font-mono flex items-center gap-2">
                  MEMUR MAZERET WEBHOOK URL
                  {dcConfigured.memur_excuse_webhook_url && <span className="text-[10px] text-emerald-400">● kayıtlı</span>}
                </Label>
                <Input type="password" value={dc.memur_excuse_webhook_url} onChange={e=>setDc({...dc,memur_excuse_webhook_url:e.target.value})} placeholder="Değiştirmek için gir" data-testid="dc-memur-excuse-input" className="bg-zinc-900 border-zinc-800 mt-1.5 font-mono" /></div>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={saveDc} data-testid="dc-save-button" className="bg-rose-600 hover:bg-rose-700">Kaydet</Button>
                <Button onClick={testDc} variant="outline" data-testid="dc-test-button" className="border-emerald-800 text-emerald-400 hover:bg-emerald-950/30">
                  Test Bildirimi Gönder
                </Button>
              </div>
              {testResult && (
                <div className="text-[11px] font-mono bg-zinc-900/60 p-3 border-l-2 border-emerald-500" data-testid="dc-test-result">
                  <div className="text-zinc-500 mb-1">// TEST SONUCU</div>
                  {Object.entries(testResult).map(([k, v]) => (
                    <div key={k}><span className="text-zinc-400">{k}:</span> <span className={v === "OK" ? "text-emerald-400" : "text-rose-400"}>{String(v)}</span></div>
                  ))}
                </div>
              )}
              <div className="text-[11px] text-zinc-500 border-l-2 border-zinc-700 pl-3 bg-zinc-900/30 py-2 space-y-1">
                <div className="text-zinc-400 font-semibold">Nasıl kurarım? (bot gerekmez)</div>
                <div>1. Discord'da mesaj göndermesini istediğin kanala git</div>
                <div>2. Kanal Ayarları (dişli ikonu) → <b>Entegrasyonlar</b> → <b>Webhooklar</b> → <b>Yeni Webhook</b></div>
                <div>3. İstersen webhook'a isim/avatar ver, sonra <b>Webhook URL'sini Kopyala</b></div>
                <div>4. Kopyaladığın URL'yi yukarıdaki ilgili alana yapıştır (duyuru/acil/mazeret için ayrı ayrı, aynı kanalı kullanacaksan aynı URL'yi hepsine yapıştırabilirsin)</div>
                <div>5. Kaydet → Test Bildirimi Gönder → kanalda mesaj görürsen aktif</div>
              </div>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
